import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { API } from '@/api'
import { TeamSnapshotWithUser } from '@/api/type-response'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { message, Modal, Trans } from '@/components'
import { SNAP_STATUS_MAP } from '@/consts/snapshot'
import { TeamRole } from '@/consts/team'
import { useTeamStore, useTeamUserRole } from '@/stores/team'
import { formatBytes, formatDatePreset } from '@/utils/formatter'

import { SnapshotDetailContent } from '../components/SnapshotDetailContent'

export function SnapshotDetailPage() {
  const { t } = useTranslation()
  const { snapshotId } = useParams<{ snapshotId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const userRole = useTeamUserRole()
  const teamStore = useTeamStore()

  const snapshotData = location.state?.snapshot as TeamSnapshotWithUser | undefined

  // Redirect if no data
  useEffect(() => {
    if (!snapshotData) {
      navigate(-1)
    }
  }, [snapshotData, navigate])

  // Don't render if no data
  if (!snapshotData) {
    return null
  }

  const snapshot = snapshotData.snapshot
  const user = snapshotData.user

  // Format data
  const id = snapshot.resID
  const name = snapshot.flag?.subject || '-'
  const size = formatBytes(
    typeof snapshot.sizeByte === 'string' ? parseInt(snapshot.sizeByte, 10) : snapshot.sizeByte,
  )
  const createTime = formatDatePreset(snapshot.requestTime, 'minute')
  const status = snapshot.status
  const ownerName = user.user.nickname || user.user.name

  // Use centralized status mapping
  const statusInfo = SNAP_STATUS_MAP[status as keyof typeof SNAP_STATUS_MAP]
  const statusLabel = t(statusInfo.labelKey)
  const statusColor = statusInfo.style

  // Handle copy to clipboard
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success(t('common.copied', 'Copied to clipboard'))
  }

  // Check if user can delete
  const canDelete = userRole === TeamRole.Admin || userRole === TeamRole.Owner

  // Handle delete
  const handleDelete = () => {
    Modal.confirm({
      title: t('teamSnapshot.detail.deleteModal.title', 'Delete Snapshot'),
      content: (
        <div className="bg-panel-gray-100 rounded-lg p-4 flex-row">
          <div className="t-sm text-secondary">
            <Trans i18nKey="teamSnapshot.detail.deleteModal.content">
              Are you sure you want to delete this snapshot? This action cannot be undone.
            </Trans>
          </div>
        </div>
      ),
      confirmBtnCfg: {
        type: 'danger',
        text: t('common.delete', 'Delete'),
      },
      className: 'w-[640px]',
    }).then(async () => {
      try {
        await API.removeTeamSharedSnapshot({
          res_id: teamStore.teamTokenData!.teamResID,
          snap_res_id: snapshot.resID,
        })
        message.success(t('teamSnapshot.detail.deleteSuccess', 'Snapshot deleted successfully'))
        navigate(-1)
      } catch (error) {
        console.error('Failed to delete snapshot:', error)
        message.error(t('teamSnapshot.detail.deleteError', 'Failed to delete snapshot'))
      }
    })
  }

  return (
    <div className="card bg-panel-white p-8 flex flex-col gap-8 h-full overflow-y-auto">
      {/* Section 1: Breadcrumb Navigation */}
      <div className="border-b border-neutral-200 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/team/snapshots/shared" className="p-1 bg-panel-gray-100 rounded-md">
              <ArrowLeftIcon className="w-6 h-6 text-tertiary" />
            </Link>
            <div className="h6">
              <span className="text-tertiary">
                <Trans i18nKey="snapshot.breadcrumb.snapshots">Snapshots</Trans>
              </span>
              <span> / </span>
              <span className="text-tertiary">
                <Trans i18nKey="snapshot.breadcrumb.teamSnapshots">Team Snapshots</Trans>
              </span>
              <span> / </span>
              <span className="text-primary">{snapshotId}</span>
            </div>
          </div>

          {/* Delete Button - Only for Admin/Owner */}
          {canDelete && (
            <button
              onClick={handleDelete}
              className="bg-error/10 text-error px-4 py-3 rounded-lg t-sb min-w-[88px]"
            >
              <Trans i18nKey="common.delete">Delete</Trans>
            </button>
          )}
        </div>
      </div>

      {/* Snapshot Detail Content */}
      <SnapshotDetailContent
        id={id}
        name={name}
        size={size}
        createTime={createTime}
        statusLabel={statusLabel}
        statusColor={statusColor}
        ownerName={ownerName}
        // TODO(backend): pass the real owner history via `ownerLog` once the API exists
        onCopyId={handleCopy}
      />
    </div>
  )
}
